import React, { useCallback, useEffect, useState } from 'react'
import {
  Grid,
  GridColumn,
  GridDataStateChangeEvent,
  GridRowClickEvent,
  GridToolbar,
} from '@progress/kendo-react-grid'
import { process, State } from '@progress/kendo-data-query'
import { Dialog } from '@progress/kendo-react-dialogs'
import { FaEdit, FaFileSignature, FaLink, FaLock } from 'react-icons/fa'
import dayjs from 'dayjs'

import TabSectionHeader from './TabSectionHeader'
import PurchaseOrderForm, { formatMoney } from '../PurchaseOrders/PurchaseOrderForm'
import { useToast } from '../../../contexts/ToastContext'
import { purchaseOrder } from '../../../data/interfaces'
import { getPurchaseOrdersByJob } from '../../../data/purchaseOrderData'

interface JobPurchaseOrdersProps {
  jobId: number
  lockEdit?: boolean
  /** Lets the job form keep its rail badge in step with the list. */
  onCountChange?: (count: number) => void
}

const JobPurchaseOrders: React.FC<JobPurchaseOrdersProps> = ({
  jobId,
  lockEdit = false,
  onCountChange,
}) => {
  const { showToast } = useToast()

  const [orders, setOrders] = useState<purchaseOrder[]>([])
  const [dataState, setDataState] = useState<State>({ take: 10, skip: 0 })
  const [dialogVisible, setDialogVisible] = useState(false)
  const [currentOrderId, setCurrentOrderId] = useState<number | null>(null)

  const fetchOrders = useCallback(async () => {
    try {
      const result = await getPurchaseOrdersByJob(jobId)
      setOrders(result)
      onCountChange?.(result.length)
    } catch (error) {
      console.error('Error fetching purchase orders:', error)
      showToast('Failed to load purchase orders', 'error')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId])

  useEffect(() => {
    if (jobId > 0) {
      fetchOrders()
    }
  }, [jobId, fetchOrders])

  const processedData = process(orders, dataState)

  const handleAdd = () => {
    if (lockEdit) {
      showToast('Cannot add purchase orders while edit is locked', 'error')
      return
    }
    setCurrentOrderId(null)
    setDialogVisible(true)
  }

  const handleOpen = (order: purchaseOrder) => {
    setCurrentOrderId(order.purchaseOrderId)
    setDialogVisible(true)
  }

  const orderNoCell = (props: any) => {
    const order: purchaseOrder = props.dataItem
    return (
      <td>
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600 }}>
          {order.orderNo || 'Draft'}
          {order.isDraft && (
            <span
              style={{
                fontWeight: 600,
                fontSize: '8pt',
                padding: '1px 6px',
                borderRadius: '10px',
                backgroundColor: '#f6c344',
                color: '#5c4600',
              }}
            >
              DRAFT
            </span>
          )}
          {order.isDelivered && (
            <span
              style={{
                fontWeight: 600,
                fontSize: '8pt',
                padding: '1px 6px',
                borderRadius: '10px',
                backgroundColor: '#dbeccb',
                color: '#3f5c22',
              }}
            >
              DELIVERED
            </span>
          )}
          {order.isPartDelivered && (
            <span
              style={{
                fontWeight: 600,
                fontSize: '8pt',
                padding: '1px 6px',
                borderRadius: '10px',
                backgroundColor: '#fbeccb',
                color: '#7a5600',
              }}
            >
              PART DELIVERED
            </span>
          )}
        </span>
      </td>
    )
  }

  const deliveredCell = (props: any) => {
    const order: purchaseOrder = props.dataItem

    if (order.isDelivered) {
      return <td>{dayjs(order.deliveredDate).format('DD/MM/YYYY')}</td>
    }

    return order.isDraft ? (
      <td style={{ color: '#8a8a8a' }}>-</td>
    ) : order.isPartDelivered ? (
      <td style={{ color: '#a37800', fontWeight: 600 }}>Part delivered</td>
    ) : (
      <td style={{ color: '#a37800' }}>Awaiting</td>
    )
  }

  const valueCell = (props: any) => <td>{formatMoney(props.dataItem.grossValue)}</td>

  const raisedCell = (props: any) => (
    <td>{dayjs(props.dataItem.createdDate).format('DD/MM/YYYY')}</td>
  )

  const allocationCell = (props: any) => {
    const order: purchaseOrder = props.dataItem

    if (!order.piecesId) {
      return <td style={{ color: '#8a8a8a' }}>-</td>
    }

    return (
      <td>
        <span
          style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'rgb(94 142 23)' }}
          title={
            order.allocationLocked
              ? 'Sent to Xero - this allocation cannot be changed'
              : 'Allocated to a Pieces invoice'
          }
        >
          {order.allocationLocked ? <FaLock /> : <FaLink />}
          {order.allocatedPiecesInvoiceId || 'Allocated'}
        </span>
      </td>
    )
  }

  const actionCell = (props: any) => (
    <td style={{ textAlign: 'center' }}>
      <FaEdit
        style={{ cursor: 'pointer', color: '#1e2a4e', fontSize: '16px' }}
        title='Open purchase order'
        onClick={() => handleOpen(props.dataItem)}
      />
    </td>
  )

  const totalValue = orders.reduce((sum, o) => sum + (o.grossValue || 0), 0)

  return (
    <div>
      <TabSectionHeader
        icon={<FaFileSignature />}
        title='Purchase Orders'
        description='Purchase orders raised against this job'
        actions={
          <button
            type='button'
            title='Add new purchase order'
            onClick={handleAdd}
            disabled={lockEdit}
            className='jobsection__action-btn'
          >
            New Purchase Order
          </button>
        }
      />
      <div style={{ padding: '0 1rem 1rem' }}>
        <Grid
          data={processedData}
          {...dataState}
          onDataStateChange={(event: GridDataStateChangeEvent) => setDataState(event.dataState)}
          onRowClick={(event: GridRowClickEvent) => handleOpen(event.dataItem)}
          style={{ height: '400px' }}
        >
          <GridToolbar>
            <span style={{ color: '#666' }}>
              Total Orders: {orders.length} | Total Value: {formatMoney(totalValue)}
            </span>
          </GridToolbar>
          <GridColumn field='orderNo' title='Order No' width='200px' cell={orderNoCell} />
          <GridColumn field='supplierName' title='Supplier' width='260px' />
          <GridColumn field='itemCount' title='Lines' width='90px' />
          <GridColumn field='grossValue' title='Total Value' width='150px' cell={valueCell} />
          <GridColumn field='createdDate' title='Raised' width='130px' cell={raisedCell} />
          <GridColumn field='deliveredDate' title='Delivered' width='130px' cell={deliveredCell} />
          <GridColumn field='piecesId' title='Pieces' width='170px' cell={allocationCell} />
          <GridColumn title='Actions' width='100px' cell={actionCell} />
        </Grid>
      </div>

      {dialogVisible && (
        <Dialog
          title={currentOrderId ? 'Purchase Order' : 'New Purchase Order'}
          onClose={() => setDialogVisible(false)}
          appendTo={document.body}
          width='820px'
        >
          <PurchaseOrderForm
            key={currentOrderId ?? 'new'}
            purchaseOrderId={currentOrderId}
            // Raised from inside a job, so the job is already settled and the
            // user is never asked for it.
            jobId={jobId}
            lockEdit={lockEdit}
            onSaved={(order) => {
              fetchOrders()
              if (order && !currentOrderId) {
                setCurrentOrderId(order.purchaseOrderId)
              }
            }}
            onClose={() => setDialogVisible(false)}
          />
        </Dialog>
      )}
    </div>
  )
}

export default JobPurchaseOrders
